import type { TypedDocumentNode } from "@apollo/client";
import type { DocumentNode } from "graphql";
import type { JSX } from "react";
import { createElement } from "react";
import { CrudOperation, CrudOperationResult } from "./crud-operation";
import { DeleteOperationView } from "./delete-operation-view";


export type DeleteOperationMutationVariables = { id: string };

export type DeletionVerb = "delete" | "remove";
export type DeleteOperationConfig = {
	id: string;
	resourceName: string;
	message?: string | JSX.Element;
	mutation: TypedDocumentNode<unknown, DeleteOperationMutationVariables>;
	verb?: DeletionVerb;
};

export class DeleteOperation extends CrudOperation<null> {
	readonly #config: Readonly<DeleteOperationConfig>;

	readonly view: () => JSX.Element;

	constructor(config: DeleteOperationConfig) {
		const verb = config.verb ?? "delete";
		super({
			titleKey: `common:${verb}Named`,
			titleValues: { name: config.resourceName },
			successMessageKey: `common:${verb}Notification`,
			successMessageValues: { name: config.resourceName },
			submitLabelKey: `common:${verb}`,
			submitLabelColor: "danger",
			secondaryAction: {
				labelKey: "common:cancel",
				onClick: () => this.services.crud.cancel(),
			},
		});
		this.#config = config;

		this.view = () => createElement(DeleteOperationView, { operation: this });
	}

	override initialise(): Promise<void> {
		return Promise.resolve();
	}

	override async submit(options: Readonly<{ refetchQueries: DocumentNode[]; }>): Promise<CrudOperationResult<null>> {
		await this.services.api.mutateOnce(this.#config.mutation, { id: this.#config.id }, {
			refetchQueries: options.refetchQueries,
		});
		return CrudOperationResult.ok(null);
	}

	override dispose(): void { /**/ }

	get verb(): DeletionVerb {
		return this.#config.verb ?? "delete";
	}

	get resourceName(): string {
		return this.#config.resourceName;
	}

	get message(): string | JSX.Element | null {
		return this.#config.message ?? null;
	}

	override hasChanges(): boolean {
	  return false;
	}
}
